"use client";

import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import { SectionShell, EditorialTitle, GradientText, RevealOnScroll } from "./_primitives";

export default function ProblemSection() {
    const t = useTranslations();

    const issues = [1, 2, 3, 4, 5].map((i) => ({
        num: String(i).padStart(2, "0"),
        title: t(`joinInstructor_problem_item${i}_title`),
        desc: t(`joinInstructor_problem_item${i}_desc`),
    }));

    return (
        <SectionShell label={t("joinInstructor_problem_marker")}>
            <RevealOnScroll>
                <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                    {t("joinInstructor_problem_eyebrow")}
                </p>
            </RevealOnScroll>

            <RevealOnScroll delay={0.05}>
                <EditorialTitle className="text-4xl sm:text-5xl md:text-7xl mb-12 sm:mb-16">
                    {t("joinInstructor_problem_title_part1")}{" "}
                    <span className="relative inline-block">
                        <span className="text-gray-400 dark:text-white/40">{t("joinInstructor_problem_title_strike")}</span>
                        <motion.span
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-primary origin-left"
                        />
                    </span>
                    <br />
                    {t("joinInstructor_problem_title_part2")}{" "}
                    <GradientText>{t("joinInstructor_problem_title_emphasis")}</GradientText>
                </EditorialTitle>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 border-t border-gray-200 dark:border-white/10 pt-8">
                {issues.map((it, i) => (
                    <RevealOnScroll key={it.num} delay={i * 0.06}>
                        <div className="h-full p-5 flex flex-col">
                            <span className="font-mono text-xs text-gray-400 dark:text-white/40 mb-4">{it.num}</span>
                            <h3
                                className="text-xl text-gray-900 dark:text-white font-bold mb-3 leading-tight"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                {it.title}
                            </h3>
                            <p className="text-gray-500 dark:text-white/55 text-sm leading-relaxed">{it.desc}</p>
                            <div className="mt-auto pt-6">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 text-primary text-xs">
                                    ✕
                                </span>
                            </div>
                        </div>
                    </RevealOnScroll>
                ))}
            </div>

            <RevealOnScroll delay={0.4}>
                <div className="mt-10 pt-8 border-t border-gray-200 dark:border-white/10 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-base"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinInstructor_problem_footer_part1")}
                    </p>
                    <p className="text-primary font-semibold">
                        {t("joinInstructor_problem_footer_part2")}
                    </p>
                </div>
            </RevealOnScroll>
        </SectionShell>
    );
}
